import { LAYER_PRECEDENCE } from '@/types/studio';
import type {
  ConfigurationLayerScope,
  ResolvedConfigurationValue,
} from '@/types/studio';

/**
 * The data behind the provenance ladder — extracted from the SVG so the logic is a pure
 * function a test can hold still.
 *
 * WHY A LADDER AT ALL. The resolver's answer to "why is my calendar semester-based" is not
 * one layer, it is a chain: the platform floor said one thing, the country default another,
 * and the institution's own override won. An administrator shown only the winner will edit
 * the wrong layer, watch nothing change, and conclude the platform is broken — see the
 * backend's ResolvedConfigurationValue comment, which calls provenance "not debug output".
 * The ladder shows every rung, in resolver order, so "further down wins" can be read off the
 * picture instead of memorised.
 *
 * WHAT THE BACKEND DOES AND DOES NOT SAY. `overriddenLayers` names the layers that set this
 * key and lost — scope, scopeKey and label — but not the values they set: the resolver keeps
 * the losers' identities, not their answers. So only the winning rung carries a value chip,
 * and a beaten rung is drawn as "spoke, was overruled" rather than with a value this client
 * would have to invent.
 */

export type LadderRungStatus =
  /** This layer supplied the value in force. */
  | 'winner'
  /** This layer set the key but a more specific layer won. */
  | 'overridden'
  /** This layer had no opinion on the key. */
  | 'silent';

export interface LadderRung {
  scope: ConfigurationLayerScope;
  status: LadderRungStatus;
  /** The layer's human label ("Cameroon national default"); null when silent. */
  label: string | null;
  /** 'CM', 'SECONDARY_SCHOOL:ANGLOPHONE', a campus id; null when silent. */
  scopeKey: string | null;
  /** The value in force. Only the winner carries one — the resolver does not report losers' values. */
  value: string | null;
}

/**
 * One rung per scope, least specific first — the exact order the resolver walks, so the
 * diagram and the algorithm cannot disagree about what "more specific" means.
 *
 * Every scope appears even when silent. A ladder that omitted silent rungs would show a
 * different shape for every key, and the reader would lose the one fixed thing this diagram
 * offers: the same six layers, in the same order, every time.
 */
export function buildLadder(resolved: ResolvedConfigurationValue): LadderRung[] {
  return LAYER_PRECEDENCE.map((scope) => {
    if (resolved.source.scope === scope) {
      return {
        scope,
        status: 'winner' as const,
        label: resolved.source.label,
        scopeKey: resolved.source.scopeKey,
        value: resolved.value,
      };
    }
    const beaten = resolved.overriddenLayers.find((layer) => layer.scope === scope);
    if (beaten) {
      return {
        scope,
        status: 'overridden' as const,
        label: beaten.label,
        scopeKey: beaten.scopeKey,
        value: null,
      };
    }
    return { scope, status: 'silent' as const, label: null, scopeKey: null, value: null };
  });
}

/**
 * SEMESTER_BASED → "Semester based".
 *
 * Enum members are server DATA, not UI copy — they arrive from the closed value sets in the
 * key registry and new members appear without a front-end release, so there is no key list
 * to translate them against. Rendering the raw code is honest but hostile; this is the raw
 * code made readable while staying recognisably the same token. Screens that need the exact
 * wire value (operators cross-referencing core's logs) keep it in a title attribute.
 */
export function humanizeValue(code: string): string {
  if (code.length === 0) return code;
  const lower = code.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
